import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Input, Tooltip } from 'antd';
import { fetchMapSettings } from '../maps/settings';
import {
  fetchAddressSuggestions,
  mountPlaceAutocompleteWidget,
  resolvePlaceDetails,
  type AddressSuggestion,
} from '../maps/addressAutocomplete';
import { geocodeAddress } from '../utils/geocode';
import { isGeocodeMockSydneyEnabled } from '../maps/mockSydneyGeocode';
import type { MapSettings } from '../maps/types';
import type { GeocodeResult } from '../types/database';
import { AddressMapPreview } from './AddressMapPreview';

export interface AddressCoordinates {
  lat: number;
  lng: number;
}

export type AddressGeocodeStatus = 'idle' | 'geocoding' | 'resolved' | 'failed' | 'no_places';

/**
 * - `antd` — Ant Design AutoComplete + Places AutocompleteService (Modal-safe fallback).
 * - `google` — Google Place Autocomplete Widget (`PlaceAutocompleteElement`).
 */
export type AddressFieldVariant = 'antd' | 'google';

export interface AddressFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  coordinates?: AddressCoordinates | null;
  onCoordinatesChange?: (coords: AddressCoordinates | null) => void;
  onStatusChange?: (status: AddressGeocodeStatus) => void;
  showMapPreview?: boolean;
  mapPreviewHeight?: number;
  mapActive?: boolean;
  variant?: AddressFieldVariant;
  placeholder?: string;
  disabled?: boolean;
  geocodeOnBlur?: boolean;
}

function applyGeocodeResult(
  result: GeocodeResult,
  onChange?: (value: string) => void,
  onCoordinatesChange?: (coords: AddressCoordinates | null) => void
) {
  onChange?.(result.formatted_address);
  onCoordinatesChange?.({ lat: result.lat, lng: result.lng });
}

type OptionItem = { value: string; label: string; placeId: string };

export function AddressField({
  value,
  onChange,
  coordinates,
  onCoordinatesChange,
  onStatusChange,
  showMapPreview = true,
  mapPreviewHeight = 160,
  mapActive = true,
  variant = 'antd',
  placeholder = 'Start typing an address…',
  disabled = false,
  geocodeOnBlur = true,
}: AddressFieldProps) {
  const [status, setStatus] = useState<AddressGeocodeStatus>(
    coordinates ? 'resolved' : 'idle'
  );
  const [options, setOptions] = useState<OptionItem[]>([]);
  const [placesReady, setPlacesReady] = useState(false);
  const settingsRef = useRef<MapSettings | null>(null);
  const geocodingRef = useRef(false);
  const selectingRef = useRef(false);
  const coordsRef = useRef(coordinates);
  coordsRef.current = coordinates;
  const suggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const widgetHostRef = useRef<HTMLDivElement>(null);
  const widgetValueRef = useRef(value);
  widgetValueRef.current = value;
  const onChangeRef = useRef(onChange);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);
  onChangeRef.current = onChange;
  onCoordinatesChangeRef.current = onCoordinatesChange;

  const updateStatus = useCallback(
    (next: AddressGeocodeStatus) => {
      setStatus(next);
      onStatusChange?.(next);
    },
    [onStatusChange]
  );

  useEffect(() => {
    if (coordinates) updateStatus('resolved');
  }, [coordinates, updateStatus]);

  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const settings = await fetchMapSettings();
        if (disposed) return;
        settingsRef.current = settings;
        if (!settings.googleApiKey) {
          setPlacesReady(false);
          updateStatus('no_places');
          return;
        }
        setPlacesReady(true);
      } catch {
        if (!disposed) {
          setPlacesReady(false);
          updateStatus('no_places');
        }
      }
    })();
    return () => {
      disposed = true;
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, [updateStatus]);

  // Google Place Autocomplete Widget — PlaceAutocompleteElement (with classic fallback).
  useEffect(() => {
    if (variant !== 'google' || !placesReady || disabled) return;
    const settings = settingsRef.current;
    const host = widgetHostRef.current;
    if (!settings?.googleApiKey || !host) return;

    let cancelled = false;
    let detach: (() => void) | undefined;

    void (async () => {
      try {
        updateStatus(coordinates ? 'resolved' : 'idle');
        const cleanup = await mountPlaceAutocompleteWidget(
          host,
          settings,
          {
            onAddress: (address) => {
              selectingRef.current = true;
              onChangeRef.current?.(address);
              updateStatus('geocoding');
              window.setTimeout(() => {
                selectingRef.current = false;
              }, 350);
            },
            onCoordinates: (coords) => {
              onCoordinatesChangeRef.current?.(coords);
              updateStatus('resolved');
            },
          },
          { placeholder, value: widgetValueRef.current }
        );
        if (cancelled) {
          cleanup();
          return;
        }
        detach = cleanup;
      } catch {
        if (!cancelled) updateStatus('no_places');
      }
    })();

    return () => {
      cancelled = true;
      detach?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount on host/settings only
  }, [variant, placesReady, disabled, placeholder, updateStatus]);

  useEffect(() => {
    if (variant !== 'google') return;
    const host = widgetHostRef.current;
    if (!host || typeof value !== 'string') return;
    const widget = host.querySelector('gmp-place-autocomplete') as
      | (HTMLElement & { value?: string })
      | null;
    const input = host.querySelector('input');
    if (widget && widget.value !== value) widget.value = value;
    else if (input && input.value !== value) input.value = value;
  }, [variant, value]);

  const runSuggestions = useCallback(async (text: string) => {
    const settings = settingsRef.current;
    if (!settings?.googleApiKey) {
      setOptions([]);
      return;
    }
    try {
      const list: AddressSuggestion[] = await fetchAddressSuggestions(text, settings);
      setOptions(
        list.map((s) => ({
          value: s.description,
          label: s.description,
          placeId: s.placeId,
        }))
      );
    } catch {
      setOptions([]);
    }
  }, []);

  const handleSearch = useCallback(
    (text: string) => {
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
      if (!text.trim() || text.trim().length < 2) {
        setOptions([]);
        return;
      }
      suggestTimerRef.current = setTimeout(() => {
        void runSuggestions(text);
      }, 250);
    },
    [runSuggestions]
  );

  const handleSelect = useCallback(
    async (_selected: string, option: OptionItem) => {
      selectingRef.current = true;
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      const placeId = option.placeId;
      const settings = settingsRef.current;
      if (!placeId || !settings) {
        selectingRef.current = false;
        return;
      }
      updateStatus('geocoding');
      try {
        const details = await resolvePlaceDetails(placeId, settings);
        if (details) {
          onChange?.(details.formattedAddress);
          onCoordinatesChange?.(details.coordinates);
          updateStatus('resolved');
          setOptions([]);
        } else {
          onCoordinatesChange?.(null);
          updateStatus('failed');
        }
      } catch {
        onCoordinatesChange?.(null);
        updateStatus('failed');
      } finally {
        window.setTimeout(() => {
          selectingRef.current = false;
        }, 350);
      }
    },
    [onChange, onCoordinatesChange, updateStatus]
  );

  const runGeocode = useCallback(async () => {
    const trimmed = value?.trim();
    if (!trimmed || coordsRef.current || geocodingRef.current || selectingRef.current) return;
    if (isGeocodeMockSydneyEnabled()) {
      onCoordinatesChange?.(null);
      updateStatus('failed');
      return;
    }
    geocodingRef.current = true;
    updateStatus('geocoding');
    try {
      const result = await geocodeAddress(trimmed);
      if (result) {
        applyGeocodeResult(result, onChange, onCoordinatesChange);
        updateStatus('resolved');
      } else {
        onCoordinatesChange?.(null);
        updateStatus('failed');
      }
    } finally {
      geocodingRef.current = false;
    }
  }, [value, onChange, onCoordinatesChange, updateStatus]);

  const allowBlurGeocode = geocodeOnBlur && variant !== 'google';
  const handleBlur = () => {
    if (!allowBlurGeocode || !value?.trim() || coordsRef.current || selectingRef.current) return;
    blurTimerRef.current = setTimeout(() => {
      if (!selectingRef.current && !coordsRef.current) void runGeocode();
    }, 300);
  };

  const suffix = useMemo(() => {
    if (status === 'geocoding') {
      return (
        <span style={{ color: '#2563EB', fontSize: 12 }} aria-hidden="true">
          …
        </span>
      );
    }
    if (status === 'resolved' && coordinates) {
      return (
        <Tooltip title="Google address verified">
          <span style={{ color: '#059669', fontWeight: 700 }} aria-label="Location verified">
            ✓
          </span>
        </Tooltip>
      );
    }
    if (status === 'failed') {
      return (
        <Tooltip title="Pick a Google suggestion so the address can be verified">
          <span style={{ color: '#DC2626', fontWeight: 700 }} aria-label="Location not verified">
            !
          </span>
        </Tooltip>
      );
    }
    if (status === 'no_places') {
      return (
        <Tooltip title="Google Places unavailable — check Maps API key">
          <span style={{ color: '#D97706', fontWeight: 700 }} aria-label="Places unavailable">
            ?
          </span>
        </Tooltip>
      );
    }
    return null;
  }, [status, coordinates]);

  const mapPreview = showMapPreview ? (
    <AddressMapPreview
      coordinates={coordinates ?? null}
      title={value?.trim() || 'Selected location'}
      height={mapPreviewHeight}
      active={mapActive}
      placeholderHint="Select a Google address suggestion to pin the map"
    />
  ) : null;

  if (variant === 'google') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            ref={widgetHostRef}
            className="ubus-place-autocomplete-host"
            style={{
              flex: 1,
              minHeight: 32,
              opacity: placesReady && !disabled ? 1 : 0.6,
              pointerEvents: placesReady && !disabled ? 'auto' : 'none',
            }}
            aria-busy={!placesReady}
          />
          {suffix}
        </div>
        {!placesReady ? (
          <div style={{ marginTop: 4, color: '#64748B', fontSize: 12 }}>
            {status === 'no_places' ? 'Google Places unavailable' : 'Loading Google Place widget…'}
          </div>
        ) : null}
        {mapPreview}
      </div>
    );
  }

  return (
    <div>
      <AutoComplete
        value={value}
        disabled={disabled}
        options={options}
        style={{ width: '100%' }}
        onSearch={handleSearch}
        onSelect={(v, opt) => {
          void handleSelect(String(v), opt as OptionItem);
        }}
        onChange={(next) => {
          onChange?.(String(next ?? ''));
          onCoordinatesChange?.(null);
          if (status !== 'geocoding') updateStatus('idle');
        }}
        onBlur={handleBlur}
        allowClear
        getPopupContainer={(trigger) =>
          (trigger.closest('.ant-modal-wrap') as HTMLElement) ||
          trigger.parentElement ||
          document.body
        }
      >
        <Input placeholder={placeholder} autoComplete="off" suffix={suffix} />
      </AutoComplete>
      {mapPreview}
    </div>
  );
}

export default AddressField;
